import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { LLM_API_PREFIX, ensureActiveLumenToken } from "@/utils/lumenauth";

const SAFETY_TIMEOUT_MS = 240_000;

export interface PdfJob {
  jobKey: string;
  kind: "message" | "report";
  chatId: string;
  at: string;
  questionPreview: string;
  startedAt: number;
}

export interface ReportResult {
  chatId: string;
  blobUrl: string;
  completedAt: number;
}

interface PdfGenerationContextValue {
  activeJob: PdfJob | null;
  error: string | null;
  lastCompletedAt: number | null;
  isBusy: boolean;
  reportResult: ReportResult | null;
  reportError: { chatId: string; message: string } | null;
  start: (opts: { jobKey: string; chatId: string; at: string; questionPreview: string }) => void;
  startReport: (opts: { chatId: string; focus: string }) => void;
  cancel: () => void;
  clearError: () => void;
}

const PdfGenerationContext = createContext<PdfGenerationContextValue | null>(null);

export function usePdfGeneration(): PdfGenerationContextValue {
  const ctx = useContext(PdfGenerationContext);
  if (!ctx) throw new Error("usePdfGeneration must be used inside PdfGenerationProvider");
  return ctx;
}

interface ProviderProps {
  children: ReactNode;
}

export function PdfGenerationProvider({ children }: ProviderProps) {
  const [activeJob, setActiveJob] = useState<PdfJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastCompletedAt, setLastCompletedAt] = useState<number | null>(null);
  const [reportResult, setReportResult] = useState<ReportResult | null>(null);
  const [reportError, setReportError] = useState<{ chatId: string; message: string } | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const activeJobKeyRef = useRef<string | null>(null);
  const userCancelledRef = useRef(false);
  const completedTimeoutRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (abortRef.current) {
      userCancelledRef.current = true;
      abortRef.current.abort();
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const launch = useCallback(
    (
      job: PdfJob,
      path: string,
      onSuccess: (blob: Blob) => void,
      onFailure: (message: string) => void,
    ): boolean => {
      if (abortRef.current && activeJobKeyRef.current === job.jobKey) return false;
      if (abortRef.current) {
        setError(
          "Another PDF is currently being generated. Cancel it first or wait for it to finish.",
        );
        return false;
      }

      activeJobKeyRef.current = job.jobKey;
      setActiveJob(job);
      setError(null);
      setLastCompletedAt(null);
      userCancelledRef.current = false;

      const ac = new AbortController();
      abortRef.current = ac;
      const safetyTimeout = window.setTimeout(() => {
        // eslint-disable-next-line no-console
        console.warn("[Ask Pulse PDF] safety timeout fired (4 min)");
        ac.abort();
      }, SAFETY_TIMEOUT_MS);
      
      (async () => {
        try {
          const session = await ensureActiveLumenToken();
          if (!session) throw new Error("Pulse session isn't ready yet — try again in a moment.");

          const url = `${import.meta.env.VITE_LUMEN_BACKEND_API}${path}`;
          // eslint-disable-next-line no-console
          console.log(`[Ask Pulse PDF] requesting ${url}`);
          let res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.token}` },
            signal: ac.signal,
          });
          if (res.status === 401) {
            // Scoped token likely expired mid-flight — re-mint once and retry
            // before treating it as a real failure (same pattern as lumenFetch).
            const retrySession = await ensureActiveLumenToken();
            if (retrySession) {
              res = await fetch(url, {
                headers: { Authorization: `Bearer ${retrySession.token}` },
                signal: ac.signal,
              });
            }
          }
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body?.error || `Server returned ${res.status}`);
          }
          onSuccess(await res.blob());
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error("[Ask Pulse PDF] export failed", err);
          if (err?.name === "AbortError") {
            if (!userCancelledRef.current) {
              onFailure("PDF generation took too long and was stopped. Please try again.");
            }
            // User-initiated cancel → clear silently.
          } else {
            onFailure(err?.message || "Failed to save PDF");
          }
        } finally {
          window.clearTimeout(safetyTimeout);
          abortRef.current = null;
          activeJobKeyRef.current = null;
          setActiveJob(null);
        }
      })();
      return true;
    },
    // No deps: reads everything through refs, so it never needs re-creating.
    [],
  );

  const start = useCallback(
    (opts: { jobKey: string; chatId: string; at: string; questionPreview: string }) => {
      const job: PdfJob = { ...opts, kind: "message", startedAt: Date.now() };
      const path = `${LLM_API_PREFIX}/chats/${encodeURIComponent(opts.chatId)}/message-pdf-llm?at=${encodeURIComponent(opts.at)}`;
      launch(
        job,
        path,
        (blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = objectUrl;
          a.download = `ask-pulse-${opts.at.replace(/[:.]/g, "-")}.pdf`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

          setLastCompletedAt(Date.now());
          if (completedTimeoutRef.current) window.clearTimeout(completedTimeoutRef.current);
          completedTimeoutRef.current = window.setTimeout(() => {
            setLastCompletedAt(null);
          }, 2_500);
        },
        (message) => setError(message),
      );
    },
    [launch],
  );

  const startReport = useCallback(
    (opts: { chatId: string; focus: string }) => {
      const job: PdfJob = {
        jobKey: `${opts.chatId}|report`,
        kind: "report",
        chatId: opts.chatId,
        at: "",
        questionPreview: opts.focus || "Consolidated conversation report",
        startedAt: Date.now(),
      };
      const focusParam = opts.focus ? `?focus=${encodeURIComponent(opts.focus)}` : "";
      const path = `${LLM_API_PREFIX}/chats/${encodeURIComponent(opts.chatId)}/report-pdf${focusParam}`;
      const started = launch(
        job,
        path,
        (blob) => {
          const blobUrl = URL.createObjectURL(blob);
          // One report preview at a time — release the previous blob.
          setReportResult((prev) => {
            if (prev) URL.revokeObjectURL(prev.blobUrl);
            return { chatId: opts.chatId, blobUrl, completedAt: Date.now() };
          });
        },
        (message) => setReportError({ chatId: opts.chatId, message }),
      );
      if (started) {
        setReportError(null);
        setReportResult((prev) => {
          if (prev && prev.chatId === opts.chatId) {
            URL.revokeObjectURL(prev.blobUrl);
            return null;
          }
          return prev;
        });
      }
    },
    [launch],
  );

  const value = useMemo<PdfGenerationContextValue>(
    () => ({
      activeJob,
      error,
      lastCompletedAt,
      isBusy: activeJob !== null,
      reportResult,
      reportError,
      start,
      startReport,
      cancel,
      clearError,
    }),
    [
      activeJob,
      error,
      lastCompletedAt,
      reportResult,
      reportError,
      start,
      startReport,
      cancel,
      clearError,
    ],
  );

  return <PdfGenerationContext.Provider value={value}>{children}</PdfGenerationContext.Provider>;
}